import { useSuspenseQuery } from "@apollo/client/react"
import { Formik } from "formik"
import React, { useEffect, useReducer } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"

import { detectBrowserLocale, loadStoredLocale, resolveInitialLocale } from "@/constants/locales"
import { useLocale } from "@/contexts/locale"
import { createPublicReservationValidationSchema } from "@/forms/public_reservation"
import { PublicVenueDocument, type PublicVenueQuery, type PublicVenueQueryVariables } from "@/graphql/graphql"
import NotFound from "@/pages/not_found"
import useIsMobile from "@/shared/use_is_mobile"

import Desktop from "./desktop"
import Mobile from "./mobile"
import type { ReservationFormValues, ModalState, ModalAction } from "./types"

export type { ReservationFormValues }

const INITIAL_RESERVATION_FORM_VALUES: ReservationFormValues = {
  date: null,
  peopleAmount: 2,
  startTime: "",
  endTime: "",
  tableId: "",
  name: "",
  email: "",
  phone: "",
  note: "",
  marketingConsent: false,
  termsAccepted: false,
}

// Form submission happens inside the booking flow; this shared Formik provider only stores page-level state.
const handleVenueFormSubmit = () => undefined

function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case "OPEN_BOOKING":
      return { ...state, bookingOpen: true, mobileMode: false }
    case "OPEN_FROM_MOBILE_BOOKING":
      return { ...state, mobileBookingOpen: false, bookingOpen: true, mobileMode: true }
    case "CLOSE_BOOKING":
      return { ...state, bookingOpen: false, mobileMode: false }
    case "BACK_TO_MOBILE_BOOKING":
      return { ...state, bookingOpen: false, mobileMode: false, mobileBookingOpen: true }
    case "OPEN_MOBILE_BOOKING":
      return { ...state, mobileBookingOpen: true }
    case "CLOSE_MOBILE_BOOKING":
      return { ...state, mobileBookingOpen: false }
    case "DISMISS_CONFIRMATION":
      return { ...state, confirmation: null }
    default:
      return state
  }
}

const Venue: React.FC = () => {
  const { slug } = useParams()
  const { setLocale } = useLocale()
  const venueData = useSuspenseQuery<PublicVenueQuery, PublicVenueQueryVariables>(PublicVenueDocument, {
    variables: { slug },
  })
  const venue = venueData.data.public.venue

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const confirmationState = searchParams.get("confirmation")
  const isMobile = useIsMobile()

  const [modalState, dispatch] = useReducer(modalReducer, {
    bookingOpen: false,
    mobileBookingOpen: false,
    mobileMode: false,
    confirmation: confirmationState === "expired" || confirmationState === "invalid" ? confirmationState : null,
  })

  useEffect(() => {
    if ((confirmationState !== "expired" && confirmationState !== "invalid") || !slug) return

    navigate(`/v/${slug}`, { replace: true })
  }, [confirmationState, navigate, slug])

  useEffect(() => {
    if (!venue) return

    const resolvedLocale = resolveInitialLocale({
      storedLocale: loadStoredLocale(),
      browserLocale: detectBrowserLocale(),
      venueLocale: venue.defaultLocale,
      country: venue.country,
    })

    setLocale(resolvedLocale)
  }, [setLocale, venue?.country, venue?.defaultLocale])

  if (!venue) {
    return <NotFound />
  }

  const venueLogoUrl = venue.images?.find((img) => img.kind === "venue_logo")?.url || null
  const validationSchema = createPublicReservationValidationSchema(venue.reservationConfiguration, venue.timezone)

  const layoutProps = { venue, venueLogoUrl, modalState, dispatch }

  return (
    <Formik
      initialValues={INITIAL_RESERVATION_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleVenueFormSubmit}
    >
      {isMobile ? <Mobile {...layoutProps} /> : <Desktop {...layoutProps} />}
    </Formik>
  )
}

export default Venue
